import React, { useEffect, useState } from 'react';
import { Card, Typography, Tag, Spin, message, Avatar, Tooltip, Space, Progress, Button } from 'antd';
import {
  CheckCircleOutlined,
  CalendarOutlined,
  RocketOutlined,
  FlagOutlined,
  PlusOutlined,
  BugOutlined,
  CheckSquareOutlined,
  StarOutlined,
  BookOutlined,
  ThunderboltOutlined,
  ExperimentOutlined,
  FireOutlined,
  BulbOutlined,
  ToolOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  CodeOutlined,
  DatabaseOutlined,
  CloudOutlined,
  LockOutlined,
  FileTextOutlined,
  AuditOutlined
} from '@ant-design/icons';
import { useSearchParams } from "react-router-dom";
import dayjs from 'dayjs';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';

const { Text } = Typography;

// Icon mapping for task types
const TASK_TYPE_ICON_MAP = {
  'check-square': <CheckSquareOutlined />,
  'bug': <BugOutlined />,
  'rocket': <RocketOutlined />,
  'star': <StarOutlined />,
  'book': <BookOutlined />,
  'thunderbolt': <ThunderboltOutlined />,
  'experiment': <ExperimentOutlined />,
  'flag': <FlagOutlined />,
  'fire': <FireOutlined />,
  'bulb': <BulbOutlined />,
  'tool': <ToolOutlined />,
  'setting': <SettingOutlined />,
  'safety-certificate': <SafetyCertificateOutlined />,
  'api': <ApiOutlined />,
  'code': <CodeOutlined />,
  'database': <DatabaseOutlined />,
  'cloud': <CloudOutlined />,
  'lock': <LockOutlined />,
  'file-text': <FileTextOutlined />,
  'audit': <AuditOutlined />,
};

const getTaskTypeIcon = (iconName) => {
  return TASK_TYPE_ICON_MAP[iconName] || <CheckSquareOutlined />;
};

// Sortable Task Card
const TaskCard = ({ task }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
  };

  const isOverdue = task.due_date && dayjs(task.due_date).isBefore(dayjs(), 'day') && !task.is_completed;
  const priorityColor = task.priority > 5 ? '#f5222d' : task.priority > 2 ? '#fa8c16' : '#52c41a';

  // Calculate timeline progress
  const getTimelineProgress = () => {
    if (task.estimated_start_date && task.estimated_end_date) {
      const start = dayjs(task.estimated_start_date);
      const end = dayjs(task.estimated_end_date);
      const today = dayjs();

      if (today.isBefore(start)) return 0;
      if (today.isAfter(end)) return 100;

      const total = end.diff(start, 'day');
      const elapsed = today.diff(start, 'day');
      return Math.round((elapsed / total) * 100);
    }
    return null;
  };

  const timelineProgress = getTimelineProgress();



  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card
        size="small"
        style={{
          marginBottom: 8,
          backgroundColor: 'white',
          borderLeft: `3px solid ${priorityColor}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
        hoverable
        onClick={(e) => {
          if (e.target.tagName !== 'BUTTON') {
            // Open TaskDrawer instead of navigating
            const params = new URLSearchParams(searchParams);
            params.set('editId', task.id);
            setSearchParams(params);
          }
        }}
      >
        {/* Task Code */}
        <div style={{ marginBottom: 6 }}>
          <Tag color="blue" style={{ fontSize: 10 }}>{task.task_code}</Tag>
          {task.task_type_name && (
            <Tag style={{ fontSize: 10 }} color={task.task_type_color}>
              {task.task_type_icon && getTaskTypeIcon(task.task_type_icon)} {task.task_type_name}
            </Tag>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 13 }}>{task.title}</Text>
        </div>

        {/* Project Name */}
        {task.project_name && (
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {task.project_code} - {task.project_name}
            </Text>
          </div>
        )}

        {/* Timeline Dates */}
        {(task.estimated_start_date || task.estimated_end_date || task.actual_start_date || task.actual_end_date) && (
          <div style={{
            marginBottom: 8,
            padding: '6px 8px',
            backgroundColor: '#f5f5f5',
            borderRadius: 4
          }}>
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {/* Estimated Timeline */}
              {(task.estimated_start_date || task.estimated_end_date) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CalendarOutlined style={{ fontSize: 10, color: '#1890ff' }} />
                  <Text style={{ fontSize: 10, color: '#595959' }}>
                    Est: {task.estimated_start_date ? dayjs(task.estimated_start_date).format('MMM DD') : '?'}
                    {' → '}
                    {task.estimated_end_date ? dayjs(task.estimated_end_date).format('MMM DD') : '?'}
                  </Text>
                </div>
              )}

              {/* Actual Timeline */}
              {(task.actual_start_date || task.actual_end_date) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {task.actual_end_date ? (
                    <CheckCircleOutlined style={{ fontSize: 10, color: '#52c41a' }} />
                  ) : (
                    <RocketOutlined style={{ fontSize: 10, color: '#fa8c16' }} />
                  )}
                  <Text style={{ fontSize: 10, color: task.actual_end_date ? '#52c41a' : '#fa8c16' }}>
                    Act: {task.actual_start_date ? dayjs(task.actual_start_date).format('MMM DD') : '?'}
                    {' → '}
                    {task.actual_end_date ? dayjs(task.actual_end_date).format('MMM DD') : 'In Progress'}
                  </Text>
                </div>
              )}

              {/* Timeline Progress Bar */}
              {timelineProgress !== null && !task.actual_end_date && (
                <Progress
                  percent={timelineProgress}
                  size="small"
                  strokeColor={
                    timelineProgress > 100 ? '#f5222d' :
                    timelineProgress > 75 ? '#fa8c16' : '#52c41a'
                  }
                  showInfo={false}
                  style={{ margin: '2px 0' }}
                />
              )}
            </Space>
          </div>
        )}

        {/* Labels */}
        {task.labels && task.labels.length > 0 && (
          <div style={{ marginBottom: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {task.labels.map(label => (
              <Tag
                key={label.id}
                color={label.color}
                style={{ margin: 0, fontSize: 10, borderRadius: 3 }}
              >
                {label.name}
              </Tag>
            ))}
          </div>
        )}

        {/* Assignees */}
        {task.task_members && task.task_members.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Space size={4} style={{ display: 'flex', alignItems: 'center' }}>
              <Avatar.Group maxCount={3} size="small">
                {task.task_members.map((member, index) => (
                  <Tooltip key={member.id} title={`${member.user_name}${member.employee_id ? ' (' + member.employee_id + ')' : ''}`}>
                    <Avatar
                      style={{
                        backgroundColor: '#1890ff',
                        fontSize: 10,
                        border: '2px solid white'
                      }}
                      size="small"
                    >
                      {member.user_name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
              </Avatar.Group>
              <Text style={{ fontSize: 10 }}>
                {task.task_members.map(m => m.user_name + (m.employee_id ? ` (${m.employee_id})` : '')).join(', ')}
              </Text>
            </Space>
          </div>
        )}

        {/* Footer with due date and checklist progress */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
          {task.due_date && (
            <Tooltip title={`Due: ${dayjs(task.due_date).format('MMMM DD, YYYY')}`}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 6px',
                backgroundColor: isOverdue ? '#fff1f0' : '#f0f5ff',
                borderRadius: 3
              }}>
                <FlagOutlined
                  style={{
                    fontSize: 10,
                    color: isOverdue ? '#f5222d' : '#1890ff',
                  }}
                />
                <Text
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: isOverdue ? '#f5222d' : '#1890ff',
                  }}
                >
                  {dayjs(task.due_date).format('MMM DD')}
                </Text>
              </div>
            </Tooltip>
          )}

          {task.checklist_progress !== null && task.checklist_progress !== undefined && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 6px',
              backgroundColor: task.checklist_progress === 100 ? '#f6ffed' : '#fafafa',
              borderRadius: 3
            }}>
              <CheckCircleOutlined style={{ fontSize: 10, color: task.checklist_progress === 100 ? '#52c41a' : '#8c8c8c' }} />
              <Text style={{ fontSize: 10, fontWeight: 500 }}>{task.checklist_progress}%</Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

// Kanban Column
const KanbanColumn = ({ status, tasks, projectId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setNodeRef } = useDroppable({
    id: status.id,
  });

  const handleCreateTask = () => {
    const params = new URLSearchParams(searchParams);
    params.set('create', 'true');
    if (projectId) {
      params.set('projectId', projectId);
    }
    if (status.id) {
      params.set('statusId', status.id);
    }
    setSearchParams(params);
  };

  return (
    <div
      style={{
        backgroundColor: status.color || '#f0f0f0',
        borderRadius: 8,
        padding: 12,
        minWidth: 280,
        maxWidth: 280,
        height: 'calc(100vh - 250px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Column Header */}
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: 14 }}>
            {status.name}
          </Text>
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 'bold',
            }}
          >
            {tasks.length}
          </div>
        </div>
      </div>

      {/* Droppable Area */}
      <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: 8 }}>
          {tasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                No tasks
              </Text>
            </div>
          ) : (
            tasks.map((task) => <TaskCard key={task.id} task={task} />)
          )}

          {/* Add Task Button at bottom of column */}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleCreateTask}
            style={{
              width: '100%',
              marginTop: 8,
              borderColor: 'rgba(0,0,0,0.2)'
            }}
            size="small"
          >
            Add Task
          </Button>
        </div>
      </SortableContext>
    </div>
  );
};

// Main Kanban Component
const TasksKanban = ({ projectId, onTaskUpdate }) => {
  const [tasks, setTasks] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [originalStatus, setOriginalStatus] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts, allowing clicks to pass through
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    } else {
      fetchAllTasksAndStatuses();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      // Fetch statuses for the specific project
      const statusesResponse = await axios.get(`/project_board/statuses/?project=${projectId}`);
      setTaskStatuses(statusesResponse.data.results || statusesResponse.data);

      // Fetch tasks for the specific project
      const tasksResponse = await axios.get(`/project_board/tasks/?project=${projectId}&page_size=1000`);
      setTasks(tasksResponse.data.results || tasksResponse.data);
    } catch (error) {
      message.error('Failed to load project data');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchAllTasksAndStatuses = async () => {
    setLoading(true);
    try {
      // Fetch all tasks
      const tasksResponse = await axios.get('/project_board/tasks/?page_size=1000');
      const allTasks = tasksResponse.data.results || tasksResponse.data;

      // Fetch all statuses from all projects
      const statusesResponse = await axios.get('/project_board/statuses/?page_size=1000');
      const allStatuses = statusesResponse.data.results || statusesResponse.data;

      // Group statuses by name - merge same-named statuses across projects
      const statusByName = {};
      allStatuses.forEach(status => {
        const key = status.name;
        if (!statusByName[key]) {
          statusByName[key] = {
            ...status,
            _statusIds: [status.id], // track all IDs that share this name
          };
        } else {
          statusByName[key]._statusIds.push(status.id);
        }
      });

      const mergedStatuses = Object.values(statusByName);

      // Remap tasks so their status points to the merged status representative ID
      const statusIdToMergedId = {};
      mergedStatuses.forEach(ms => {
        ms._statusIds.forEach(id => {
          statusIdToMergedId[id] = ms.id;
        });
      });

      const remappedTasks = allTasks.map(task => ({
        ...task,
        _originalStatus: task.status,
        status: statusIdToMergedId[task.status] || task.status,
      }));

      setTasks(remappedTasks);
      setTaskStatuses(mergedStatuses);
    } catch (error) {
      message.error('Failed to load tasks and statuses');
      console.error(error);
    }
    setLoading(false);
  };

  const handleDragStart = (event) => {
    const task = tasks.find(t => t.id === event.active.id);
    setActiveId(event.active.id);
    setOriginalStatus(task?.status);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeTask = tasks.find(t => t.id === activeId);
    if (!activeTask) return;

    // Check if over is a status column
    const overStatus = taskStatuses.find(s => s.id === overId);
    if (overStatus) {
      // Dropped on a column
      if (activeTask.status !== overStatus.id) {
        const updatedTasks = tasks.map(task => {
          if (task.id === activeId) {
            return { ...task, status: overStatus.id, status_name: overStatus.name, status_color: overStatus.color };
          }
          return task;
        });
        setTasks(updatedTasks);
      }
      return;
    }

    // Over is a task card
    const overTask = tasks.find(t => t.id === overId);
    if (!overTask) return;

    const activeIndex = tasks.findIndex(t => t.id === activeId);
    const overIndex = tasks.findIndex(t => t.id === overId);

    if (activeTask.status === overTask.status) {
      // Reorder within same status
      const reorderedTasks = arrayMove(tasks, activeIndex, overIndex);
      setTasks(reorderedTasks);
    } else {
      // Move to different status
      const updatedTasks = tasks.map(task => {
        if (task.id === activeId) {
          return { ...task, status: overTask.status, status_name: overTask.status_name, status_color: overTask.status_color };
        }
        return task;
      });
      setTasks(updatedTasks);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    const activeId = active.id;

    if (!over) {
      setActiveId(null);
      setOriginalStatus(null);
      // Revert changes if dropped outside
      if (projectId) {
        fetchProjectData();
      } else {
        fetchAllTasksAndStatuses();
      }
      return;
    }

    // Get the current task state (after optimistic updates)
    const currentTask = tasks.find(t => t.id === activeId);
    if (!currentTask) {
      setActiveId(null);
      setOriginalStatus(null);
      return;
    }

    const newStatusId = currentTask.status;

    // Check if status actually changed from original
    const statusChanged = originalStatus && newStatusId !== originalStatus;

    setActiveId(null);

    // Update backend if status changed
    if (statusChanged) {
      try {
        // Calculate new order based on position in the new status
        const tasksInNewStatus = tasks.filter(t => t.status === newStatusId);
        const newOrder = tasksInNewStatus.findIndex(t => t.id === activeId);

        // For all-tasks view, resolve the correct project-specific status ID
        let backendStatusId = newStatusId;
        if (!projectId) {
          const targetMergedStatus = taskStatuses.find(s => s.id === newStatusId);
          if (targetMergedStatus && targetMergedStatus._statusIds) {
            const taskProject = currentTask.project;
            try {
              const res = await axios.get(`/project_board/statuses/?project=${taskProject}&page_size=100`);
              const projectStatuses = res.data.results || res.data;
              const match = projectStatuses.find(s => s.name === targetMergedStatus.name);
              if (match) {
                backendStatusId = match.id;
              }
            } catch (e) {
              console.error('Failed to resolve project status', e);
            }
          }
        }

        await axios.patch(`/project_board/tasks/${activeId}/`, {
          status: backendStatusId,
          column_order: newOrder
        });

        message.success('Task moved successfully');
        onTaskUpdate && onTaskUpdate();
      } catch (error) {
        message.error('Failed to update task');
        console.error(error);
        // Revert changes
        if (projectId) {
          fetchProjectData();
        } else {
          fetchAllTasksAndStatuses();
        }
      }
    }

    setOriginalStatus(null);
  };

  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 20 }}>
          {taskStatuses.map((taskStatus) => {
            const statusTasks = tasks.filter(task => task.status === taskStatus.id);
            return (
              <KanbanColumn
                key={taskStatus.id}
                status={taskStatus}
                tasks={statusTasks}
                projectId={projectId}
              />
            );
          })}
        </div>

        <DragOverlay>
          {activeTask ? <TaskCard task={activeTask} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default TasksKanban;
